import type { CustomerDetail, DeviceRow, InvoiceRow } from '../views/CustomerDetail';
import type { DeviceProfile, LineDevice } from '../views/LineView';
import type { Design } from '../views/DesignsView';
import type { LabelReference, LabelResult, LabelZone } from '../views/LabelsView';
import type { UserDetail } from '../views/UserDetail';
import type {
  Annotation,
  AnnotationQueueItem,
  CreateAnnotationInput,
  CreateDefectTypeInput,
  CreateProductInput,
  DefectType,
  FeatureGrant,
  Health,
  Inspection,
  InspectResult,
  CreateExportInput,
  CreateSitePageInput,
  Customer,
  SitePage,
  UpdateSitePageInput,
  CreateImageSetInput,
  DatasetExport,
  ImageSet,
  ImageSetItem,
  ListInspectionsResult,
  Model,
  Product,
  Station,
  StationDetail,
  Proposal,
  StatsOverview,
  CustomerReports,
  StatsWindow,
  SyncStatus,
  UserProfile,
  UpdateDefectTypeInput,
  UpdateProductInput,
  Verdict,
} from '@visionqc/shared';

// Relative base: the vite dev proxy and the prod static server both forward
// /api and /ws to the backend, so the browser stays single-origin.
const BASE = '';

async function req<T>(path: string, init?: RequestInit): Promise<T> {
  // Only declare a JSON body when there actually is one. Announcing
  // application/json on a bodyless PUT/DELETE makes Fastify reject it with
  // "Body cannot be empty" — which silently broke every toggle-style endpoint,
  // and only from the browser: curl sends no content-type, so it looked fine.
  const isJsonBody = init?.body !== undefined && !(init.body instanceof FormData);
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers: isJsonBody ? { 'content-type': 'application/json', ...init?.headers } : init?.headers,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText }));
    // A validation error carries the offending fields in `issues`; surfacing the
    // first one turns a useless "Request validation failed" into "customerId: A
    // CUSTOMER login must belong to a customer", which is what the person needs.
    const issue = Array.isArray(body.issues) ? body.issues[0] : null;
    const detail = issue
      ? `${(issue.path ?? []).join('.') || 'field'}: ${issue.message}`
      : null;
    throw new Error(detail ? `${body.message}: ${detail}` : body.message || `Request failed: ${res.status}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const api = {
  health: () => req<Health>('/api/health'),

  // Products
  listProducts: () => req<Product[]>('/api/products'),
  createProduct: (input: CreateProductInput) =>
    req<Product>('/api/products', { method: 'POST', body: JSON.stringify(input) }),
  updateProduct: (id: number, input: UpdateProductInput) =>
    req<Product>(`/api/products/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),

  // ── Defect types ──
  // The library is global; each product applies a subset of it.
  listDefectLibrary: () => req<DefectType[]>('/api/defect-types'),
  createLibraryDefectType: (input: CreateDefectTypeInput) =>
    req<DefectType>('/api/defect-types', { method: 'POST', body: JSON.stringify(input) }),
  updateDefectType: (defectTypeId: number, input: UpdateDefectTypeInput) =>
    req<DefectType>(`/api/defect-types/${defectTypeId}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  deleteLibraryDefectType: (defectTypeId: number) =>
    req<void>(`/api/defect-types/${defectTypeId}`, { method: 'DELETE' }),

  /** The vocabulary applicable to one product (what the annotator shows). */
  listDefectTypes: (productId: number) =>
    req<DefectType[]>(`/api/products/${productId}/defect-types`),
  /** Create in the library AND apply to this product (annotator inline add). */
  createDefectType: (productId: number, input: CreateDefectTypeInput) =>
    req<DefectType>(`/api/products/${productId}/defect-types`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  applyDefectType: (productId: number, defectTypeId: number) =>
    req<{ ok: boolean }>(`/api/products/${productId}/defect-types/${defectTypeId}`, {
      method: 'PUT',
    }),
  unapplyDefectType: (productId: number, defectTypeId: number) =>
    req<void>(`/api/products/${productId}/defect-types/${defectTypeId}`, { method: 'DELETE' }),

  // Inspect. `setId` files the image under a named image set (Dataset page).
  inspect: (productId: number, image: Blob, filename = 'capture.jpg', setId?: number) => {
    const fd = new FormData();
    fd.append('productId', String(productId));
    fd.append('image', image, filename);
    if (setId) fd.append('setId', String(setId));
    return req<InspectResult>('/api/inspect', { method: 'POST', body: fd });
  },

  // Image sets
  listImageSets: (productId?: number, q?: string) => {
    const params = new URLSearchParams();
    if (productId) params.set('productId', String(productId));
    if (q) params.set('q', q);
    const qs = params.toString();
    return req<ImageSet[]>(`/api/image-sets${qs ? `?${qs}` : ''}`);
  },
  createImageSet: (input: CreateImageSetInput) =>
    req<ImageSet>('/api/image-sets', { method: 'POST', body: JSON.stringify(input) }),
  imageSetItems: (setId: number) => req<ImageSetItem[]>(`/api/image-sets/${setId}/items`),
  /** Label every image in a dataset at once. `label: null` clears them. */
  labelImageSet: (
    setId: number,
    label: 'GOOD' | 'BAD' | 'UNSURE' | null,
    opts: { defectTypeId?: number | null; onlyUnlabelled?: boolean } = {},
  ) =>
    req<{ updated: number; cleared: number }>(`/api/image-sets/${setId}/label`, {
      method: 'PUT',
      body: JSON.stringify({ label, ...opts }),
    }),

  /** Move every image in a dataset to another product. */
  setImageSetCustomer: (setId: number, customerId: number) =>
    req<{ ok: true }>(`/api/image-sets/${setId}/customer`, {
      method: 'PUT',
      body: JSON.stringify({ customerId }),
    }),

  setImageSetItemsProduct: (setId: number, itemIds: number[], productId: number) =>
    req<{ moved: number }>(`/api/image-sets/${setId}/items/product`, {
      method: 'PUT',
      body: JSON.stringify({ itemIds, productId }),
    }),

  setImageSetProduct: (setId: number, productId: number) =>
    req<{ moved: number }>(`/api/image-sets/${setId}/product`, {
      method: 'PUT',
      body: JSON.stringify({ productId }),
    }),
  deleteImageSet: (setId: number) =>
    req<void>(`/api/image-sets/${setId}`, { method: 'DELETE' }),

  // Inspections
  listInspections: (params: {
    productId?: number;
    verdict?: Verdict;
    defectTypeId?: number;
    from?: string;
    to?: string;
    cursor?: string;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => v !== undefined && v !== '' && q.set(k, String(v)));
    return req<ListInspectionsResult>(`/api/inspections?${q.toString()}`);
  },

  // Stats
  stats: (window: StatsWindow, productId?: number, customerId?: number) => {
    const q = new URLSearchParams({ window });
    if (productId) q.set('productId', String(productId));
    if (customerId) q.set('customerId', String(customerId));
    return req<StatsOverview>(`/api/stats/overview?${q.toString()}`);
  },

  /** Every customer with their products underneath — see ReportingView. */
  customerReports(window: StatsWindow, customerId?: number): Promise<CustomerReports> {
    const q = new URLSearchParams({ window });
    if (customerId) q.set('customerId', String(customerId));
    return req<CustomerReports>(`/api/stats/customers?${q.toString()}`);
  },

  analytics: (window: string) =>
    req<Record<string, unknown>>(`/api/analytics/overview?window=${window}`),

  // Website CMS
  listSitePages: () => req<SitePage[]>('/api/site/admin/pages'),
  createSitePage: (input: CreateSitePageInput) =>
    req<SitePage>('/api/site/admin/pages', { method: 'POST', body: JSON.stringify(input) }),
  updateSitePage: (id: number, input: UpdateSitePageInput) =>
    req<SitePage>(`/api/site/admin/pages/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteSitePage: (id: number) =>
    req<void>(`/api/site/admin/pages/${id}`, { method: 'DELETE' }),
  listPageRevisions: (pageId: number) =>
    req<Array<{ id: string; title: string; savedBy: string | null; createdAt: string; blockCount: number | null }>>(
      `/api/site/admin/pages/${pageId}/revisions`,
    ),
  restorePageRevision: (pageId: number, revisionId: string) =>
    req<SitePage>(`/api/site/admin/pages/${pageId}/revisions/${revisionId}/restore`, {
      method: 'POST',
    }),
  uploadSiteMedia: (file: File) => {
    const fd = new FormData();
    fd.append('image', file, file.name);
    return req<{ key: string; url: string }>('/api/site/admin/media', { method: 'POST', body: fd });
  },

  /** The current annotation for an image, so saved work can be reopened. */
  annotationByImage: (imageKey: string) =>
    req<Annotation | null>(`/api/annotations/by-image?imageKey=${encodeURIComponent(imageKey)}`),

  /**
   * Delete images and everything attached to them. Accepts ids, a whole
   * product or a whole dataset — removing hundreds of test images one at a
   * time is not a thing to ask of anyone.
   */
  deleteInspections: (input: {
    ids?: string[];
    productId?: number;
    setId?: number;
    annotatedBy?: string;
  }) =>
    req<{ deleted: number; objectsRemoved: number }>('/api/inspections/delete', {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  // ── Users ──
  listUsers: () => req<UserProfile[]>('/api/users'),
  /** One user plus what they have actually done. */
  userDetail: (id: number) => req<UserDetail>(`/api/users/${id}/detail`),
  createUser: (input: Record<string, unknown>) =>
    req<UserProfile>('/api/users', { method: 'POST', body: JSON.stringify(input) }),
  updateUser: (id: number, input: Record<string, unknown>) =>
    req<UserProfile>(`/api/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteUser: (id: number) => req<void>(`/api/users/${id}`, { method: 'DELETE' }),
  /** Setting a password ends that user's sessions, so it is its own call. */
  setUserPassword: (id: number, password: string) =>
    req<{ ok: boolean }>(`/api/users/${id}/password`, {
      method: 'PUT',
      body: JSON.stringify({ password }),
    }),
  setUserPin: (id: number, pin: string) =>
    req<{ ok: boolean }>(`/api/users/${id}/pin`, { method: 'PUT', body: JSON.stringify({ pin }) }),
  unlockUser: (id: number) => req<UserProfile>(`/api/users/${id}/unlock`, { method: 'POST' }),

  // ── Line devices ──
  listLineDevices: (stationId?: number, designId?: number) =>
    req<LineDevice[]>(
      `/api/line-devices${
        designId ? `?designId=${designId}` : stationId ? `?stationId=${stationId}` : ''
      }`,
    ),

  // ── Label inspection ──
  listLabelReferences: (productId: number) =>
    req<LabelReference[]>(`/api/label-references?productId=${productId}`),
  uploadLabelReference: (productId: number, file: File, name?: string) => {
    const fd = new FormData();
    fd.append('productId', String(productId));
    if (name) fd.append('name', name);
    fd.append('image', file, file.name);
    return req<LabelReference>('/api/label-references', { method: 'POST', body: fd });
  },
  setLabelZones: (id: number, zones: LabelZone[], minAlign?: number) =>
    req<LabelReference>(`/api/label-references/${id}/zones`, {
      method: 'PUT',
      body: JSON.stringify({ zones, ...(minAlign !== undefined ? { minAlign } : {}) }),
    }),
  activateLabelReference: (id: number) =>
    req<LabelReference>(`/api/label-references/${id}/activate`, { method: 'POST' }),
  /** Check one pack against the product's active reference. */
  inspectLabel: (productId: number, file: File) => {
    const fd = new FormData();
    fd.append('productId', String(productId));
    fd.append('image', file, file.name);
    return req<LabelResult>('/api/label-inspect', { method: 'POST', body: fd });
  },
  listLabelInspections: (productId: number, limit = 50) =>
    req<Record<string, unknown>[]>(`/api/label-inspections?productId=${productId}&limit=${limit}`),

  // ── Line designs (proposals) ──
  listDesigns: (customerId?: number) =>
    req<Design[]>(`/api/designs${customerId ? `?customerId=${customerId}` : ''}`),
  createDesign: (input: Record<string, unknown>) =>
    req<Design>('/api/designs', { method: 'POST', body: JSON.stringify(input) }),
  updateDesign: (id: number, input: Record<string, unknown>) =>
    req<Design>(`/api/designs/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteDesign: (id: number) => req<void>(`/api/designs/${id}`, { method: 'DELETE' }),
  designProposal: (id: number) =>
    req<{ design: Design; proposal: Proposal }>(`/api/designs/${id}/proposal`),
  /** Send the design to the invoicing system as a draft quote. */
  quoteDesign: (id: number) =>
    req<{ lines: number; unpriced: string[]; quote: Record<string, unknown> }>(
      `/api/designs/${id}/quote`,
      { method: 'POST' },
    ),
  /** The quote document, proxied — no second login, no tenant to pick wrongly. */
  designQuote: (id: number) => req<Record<string, unknown>>(`/api/designs/${id}/quote-document`),
  designQuotePdfUrl: (id: number) => `/api/designs/${id}/quote-pdf`,

  /** Lock in the prices this design was quoted at, and mark it sent. */
  freezeDesignPrices: (id: number) =>
    req<{ frozen: number }>(`/api/designs/${id}/freeze-prices`, { method: 'POST' }),
  /** Won: turn the proposal into a real station, moving its devices across. */
  buildDesign: (id: number, stationName: string, lineName?: string) =>
    req<{ stationId: number; devicesMoved: number; key: string; note: string }>(
      `/api/designs/${id}/build`,
      { method: 'POST', body: JSON.stringify({ stationName, lineName }) },
    ),
  createLineDevice: (input: Record<string, unknown>) =>
    req<LineDevice>('/api/line-devices', { method: 'POST', body: JSON.stringify(input) }),
  updateLineDevice: (id: number, input: Record<string, unknown>) =>
    req<LineDevice>(`/api/line-devices/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  deleteLineDevice: (id: number) => req<void>(`/api/line-devices/${id}`, { method: 'DELETE' }),
  /** The line as a bill of materials — what is installed, and what is unproven. */
  lineSummary: (stationId?: number, designId?: number) =>
    req<{
      items: Array<Record<string, unknown>>;
      counts: Record<string, number>;
      total: number;
      unverified: number;
      incomplete: number;
      disabled: number;
    }>(
      `/api/line-devices/summary${
        designId ? `?designId=${designId}` : stationId ? `?stationId=${stationId}` : ''
      }`,
    ),

  /** What still stands between this device and being trusted to fire. */
  deviceReadiness: (id: number) =>
    req<{ ready: boolean; problems: string[] }>(`/api/line-devices/${id}/readiness`),

  listDeviceProfiles: (role?: string) =>
    req<DeviceProfile[]>(`/api/device-profiles${role ? `?role=${role}` : ''}`),
  createDeviceProfile: (input: Record<string, unknown>) =>
    req<DeviceProfile>('/api/device-profiles', { method: 'POST', body: JSON.stringify(input) }),
  updateDeviceProfile: (id: number, input: Record<string, unknown>) =>
    req<DeviceProfile>(`/api/device-profiles/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  /** Push priced equipment to the invoicing system as products, keyed on SKU. */
  syncCatalogue: () =>
    req<{ pushed: number; created: number; updated: number; skipped: string[] }>(
      '/api/catalogue/sync',
      { method: 'POST' },
    ),
  /** Register the software plans (QC editions + per-line subs) in invoicing.
   *  Dry run unless live=true; the dry run returns exactly what would be sent. */
  syncPlans: (live = false) =>
    req<{
      dryRun: boolean;
      wouldPush?: number;
      pushed?: number;
      created?: number;
      updated?: number;
      products?: Array<{ sku: string; name: string; unit: string; unitPriceHt: number }>;
    }>('/api/catalogue/sync-plans', {
      method: 'POST',
      body: JSON.stringify({ live }),
    }),
  deleteDeviceProfile: (id: number) =>
    req<void>(`/api/device-profiles/${id}`, { method: 'DELETE' }),
  verifyDeviceProfile: (id: number, verified: boolean) =>
    req<DeviceProfile>(`/api/device-profiles/${id}/verify`, {
      method: 'POST',
      body: JSON.stringify({ verified }),
    }),

  // ── Fleet (central) ──
  listStations: () => req<Station[]>('/api/central/stations'),
  createStation: (input: { name: string; customerId: number }) =>
    // The key comes back exactly once; central only ever stores its hash.
    req<{ id: number; stationUid: string; key: string }>('/api/central/stations', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  updateStation: (id: number, input: Record<string, unknown>) =>
    req<{ ok: boolean }>(`/api/central/stations/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
    }),
  station: (id: number) => req<StationDetail>(`/api/central/stations/${id}`),
  syncStatus: () => req<SyncStatus>('/api/sync/status'),
  runSync: () => req<SyncStatus>('/api/sync/run', { method: 'POST' }),
  publishModel: (id: number, published: boolean) =>
    req<{ ok: boolean; version: string; published: boolean }>(
      `/api/central/models/${id}/publish`,
      { method: 'POST', body: JSON.stringify({ published }) },
    ),

  // Customers
  listCustomers: () => req<Customer[]>('/api/customers'),
  customerDetail: (id: number) => req<CustomerDetail>(`/api/customers/${id}/detail`),
  /** Live from the invoicing system — never a mirrored copy of a ledger. */
  customerInvoices: (id: number) =>
    req<{ configured: boolean; error?: string; invoices: InvoiceRow[] }>(
      `/api/customers/${id}/invoices`,
    ),
  /** One invoice with its lines, proxied — no second login, no tenant switch. */
  invoiceDetail: (customerId: number, invoiceId: number) =>
    req<Record<string, unknown>>(`/api/customers/${customerId}/invoices/${invoiceId}`),
  invoicePdfUrl: (customerId: number, invoiceId: number) =>
    `/api/customers/${customerId}/invoices/${invoiceId}/pdf`,
  closeUsagePeriod: (customerId: number, period: string, close: boolean) =>
    req<Record<string, unknown>>('/api/usage/periods', {
      method: 'POST',
      body: JSON.stringify({ customerId, period, close }),
    }),
  invoiceUsagePeriod: (periodId: number) =>
    req<Record<string, unknown>>(`/api/usage/periods/${periodId}/invoice`, { method: 'POST' }),
  createCustomer: (input: { name: string; notes?: string | null }) =>
    req<Customer>('/api/customers', { method: 'POST', body: JSON.stringify(input) }),
  updateCustomer: (id: number, input: Record<string, unknown>) =>
    req<Customer>(`/api/customers/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
  setProductCustomer: (productId: number, customerId: number | null) =>
    req<{ ok: boolean }>(`/api/products/${productId}/customer`, {
      method: 'PUT',
      body: JSON.stringify({ customerId }),
    }),
  /** Which functions this tenant may use, at what level. */
  customerFeatures: (id: number) =>
    req<{ grants: FeatureGrant[] }>(`/api/customers/${id}/features`),
  setCustomerFeatures: (id: number, grants: FeatureGrant[]) =>
    req<{ grants: FeatureGrant[] }>(`/api/customers/${id}/features`, {
      method: 'PUT',
      body: JSON.stringify({ grants }),
    }),
  /** On-site devices that auto-sign this customer in. */
  customerDevices: (id: number) =>
    req<{ devices: DeviceRow[] }>(`/api/customers/${id}/devices`),
  issueDevice: (id: number, userId: number, name: string) =>
    req<{ id: number; key: string }>(`/api/customers/${id}/devices`, {
      method: 'POST',
      body: JSON.stringify({ userId, name }),
    }),
  revokeDevice: (id: number, deviceId: number) =>
    req<{ ok: boolean }>(`/api/customers/${id}/devices/${deviceId}/revoke`, { method: 'POST' }),
  rotateDevice: (id: number, deviceId: number) =>
    req<{ key: string }>(`/api/customers/${id}/devices/${deviceId}/rotate`, { method: 'POST' }),
  customerLogoUrl: (id: number) => `/api/customers/${id}/logo`,
  uploadCustomerLogo: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('image', file);
    return req<{ logoKey: string }>(`/api/customers/${id}/logo`, { method: 'POST', body: fd });
  },
  clearCustomerLogo: (id: number) =>
    req<{ ok: boolean }>(`/api/customers/${id}/logo`, { method: 'DELETE' }),

  // Images are loaded directly via <img src="/api/images/<key>"> (see useImageUrl).

  // ── Models & training ──
  listModels: (productId?: number) =>
    req<Model[]>(`/api/models${productId ? `?productId=${productId}` : ''}`),
  uploadModel: (productId: number, file: File, version: string, metrics?: string) => {
    const fd = new FormData();
    fd.append('productId', String(productId));
    fd.append('version', version);
    if (metrics) fd.append('metrics', metrics);
    fd.append('model', file, file.name);
    return req<Model>('/api/models', { method: 'POST', body: fd });
  },
  activateModel: (id: number) => req<Model>(`/api/models/${id}/activate`, { method: 'POST' }),
  deactivateModel: (id: number) => req<Model>(`/api/models/${id}/deactivate`, { method: 'POST' }),
  createExport: (input: CreateExportInput) =>
    req<DatasetExport>('/api/datasets/export', { method: 'POST', body: JSON.stringify(input) }),
  listExports: (productId?: number) =>
    req<DatasetExport[]>(`/api/datasets/exports${productId ? `?productId=${productId}` : ''}`),

  // Annotations
  annotationQueue: (productId?: number, limit = 25, setId?: number) => {
    const q = new URLSearchParams({ limit: String(limit) });
    if (productId) q.set('productId', String(productId));
    if (setId) q.set('setId', String(setId));
    return req<AnnotationQueueItem[]>(`/api/annotations/queue?${q.toString()}`);
  },
  createAnnotation: (input: CreateAnnotationInput) =>
    req<Annotation>('/api/annotations', { method: 'POST', body: JSON.stringify(input) }),
};

export type { Inspection };
